import { loanChatRoute } from "@/components/report/buy/loan/loanRelayChatAccess";

export class ViewRouter {
  /**
   * 전월세 리포트 상세 화면 진입.
   * @param {Object} params - 파라미터 객체.
   * @param {string} params.encrypted_order_seq - 암호화된 주문 시퀀스.
   * @param {string} params.encrypted_cart_seq - 암호화된 카트 시퀀스.
   * @param {string} params.is_report_owner - 리포트 소유자 여부.
   * @param {string} params.is_pass - 패스 여부.
   * @returns {string} 리포트 상세 화면 경로.
   */
  static getJeonseReportRoute(params) {
    const { encrypted_order_seq, encrypted_cart_seq, is_report_owner, is_pass } = params;
    const isReportOwner = [true, "true"].includes(is_report_owner);
    const isPass = [true, "true"].includes(is_pass);
    return (
      `/report` +
      `?document=${encodeURIComponent(encrypted_order_seq)}` +
      `&seq=${encodeURIComponent(encrypted_cart_seq)}` +
      `&report_owner=${encodeURIComponent(isReportOwner)}` +
      `&pass=${encodeURIComponent(isPass)}`
    );
  }

  /**
   * 매매 리포트 상세 화면 진입.
   * @param {Object} params - 파라미터 객체.
   * @param {string} params.encrypted_order_seq - 암호화된 주문 시퀀스.
   * @param {string} params.encrypted_cart_seq - 암호화된 카트 시퀀스.
   * @param {string} params.is_report_owner - 리포트 소유자 여부.
   * @param {string} params.is_pass - 패스 여부.
   * @returns {string} 리포트 상세 화면 경로.
   */
  static getBuyReportRoute(params) {
    const { encrypted_order_seq, encrypted_cart_seq, is_report_owner, is_pass } = params;
    const isReportOwner = [true, "true"].includes(is_report_owner);
    const isPass = [true, "true"].includes(is_pass);
    return (
      `/report/buy-renewed` +
      `?document=${encodeURIComponent(encrypted_order_seq)}` +
      `&seq=${encodeURIComponent(encrypted_cart_seq)}` +
      `&report_owner=${encodeURIComponent(isReportOwner)}` +
      `&pass=${encodeURIComponent(isPass)}`
    );
  }

  /**
   * 집주인이 가지고 있는 다른집 상세 화면.
   * @param {Object} params - 파라미터 객체.
   * @param {string} params.encrypted_report_seq - 암호화된 리포트 시퀀스.
   * @returns {string} 리포트 상세 화면 경로.
   */
  static getOwnerOtherHouseViewRoute(params) {
    const { encrypted_report_seq } = params;
    return `/report/owner-house/analyze` + `?document=${encodeURIComponent(encrypted_report_seq)}`;
  }

  /**
   * 주택담보대출 상담 화면 진입.
   * acReportPre(encodeURIComponent(cartSeq)) 호출 후 진입.
   * NZSC-3277: 구 폴링 문의챗(/loan/consult/chat) 하드코딩이 NZSC-3178 통일(07-01)에서 누락돼
   * 앱 푸시 nudge·배너·팝업 광고가 답변 경로 없는 구 챗으로 유입되던 것을 loan-relay로 통일.
   * loanChatRoute가 풀오픈 플래그를 따르므로 롤백 시(플래그 off) 기존 구 챗 경로로 자동 복귀.
   * @param {number|string|null} reportSeq - 해당 리포트 seq(있으면 그 리포트의 상담 복원).
   * @param {boolean} isInternalTester - 내부 테스터 여부(풀오픈 상태에선 결과에 영향 없음).
   * @returns {string|Object} 주택담보대출 상담 화면 경로.
   */
  static getHouseLoanConsultationRoute(reportSeq = null, isInternalTester = false) {
    return loanChatRoute(isInternalTester, reportSeq);
  }

  /**
   * 리포트 발급 화면 진입.
   * @returns {string} 리포트 발급 화면 경로.
   */
  static getReportIssueRoute() {
    return `/analyze/type`;
  }

  /**
   * 구독 분석 상세 화면 진입.
   * @param {Object} params - 파라미터 객체.
   * @param {string} params.subscribe_analyze_seq - 구독 분석 시퀀스.
   * @returns {string} 구독 분석 상세 화면 경로.
   */
  static getSubscribeAnalyzeRoute(params) {
    const { subscribe_analyze_seq } = params;
    return `/subscribe/watching` + `?id=${encodeURIComponent(subscribe_analyze_seq)}`;
  }

  /**
   * 프리미엄 구독 분석 상세 화면 진입.
   * @param {Object} params - 파라미터 객체.
   * @param {string} params.subscribe_analyze_seq - 구독 분석 시퀀스.
   * @returns {string} 구독 분석 상세 화면 경로.
   */
  static getPremiumSubscribeAnalyzeRoute(params) {
    const { subscribe_analyze_seq } = params;
    return `/subscribe/premium/watching` + `?id=${encodeURIComponent(subscribe_analyze_seq)}`;
  }

  /**
   * 도비 대시보드 화면 진입.
   * @param {Object} params - 파라미터 객체.
   * @param {string} params.encrypted_doby_seq - 암호화된 도비 시퀀스.
   * @returns {string} 도비 대시보드 화면 경로.
   */
  static getDobyDashboardRoute(params) {
    const { encrypted_doby_seq } = params;
    return `/doby/dashboard` + `?id=${encodeURIComponent(encrypted_doby_seq)}`;
  }

  /**
   * 도비 랜딩 화면 진입.
   * @param {Object} params - 파라미터 객체.
   * @returns {string} 도비 랜딩 화면 경로.
   */
  static getDobyLandingRoute() {
    return `/doby/landing`;
  }

  /**
   * 미소 제휴 링크 획득.
   * window.open(link, '_blank') 로 이동.
   * @param {Object} params - 파라미터 객체.
   * @param {string} params.miso_type - 미소 제휴 링크 타입. (moving | internet |cleaning)
   * @returns {string} 미소 제휴 링크.
   */
  static getMisoRoute(params) {
    const { miso_type } = params;
    if (miso_type === "moving") {
      return `https://get.miso.kr/yAEy2YFJzKb`;
    } else if (miso_type === "internet") {
      return `https://get.miso.kr/79x1kBeKzKb`;
    } else if (miso_type === "cleaning") {
      return `https://get.miso.kr/yAEy2YFJzKb`;
    } else {
      return `https://get.miso.kr/yAEy2YFJzKb`;
    }
  }

  /**
   * 메링 제휴 링크 획득.
   * window.open(link, '_blank') 로 이동.
   * @param {Object} params - 파라미터 객체.
   * @param {string} params.marrying_type - 메링 제휴 링크 타입. (app | ledger)
   * @returns {string} 메링 제휴 링크.
   */
  static getMarryingRoute(params) {
    const { marrying_type } = params;
    if (marrying_type === "app") {
      return `https://marrying.io/app`;
    } else if (marrying_type === "ledger") {
      return `https://marrying.io/ledger`;
    } else {
      return `https://marrying.io/app`;
    }
  }
}
